import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { transitionOrderAction } from '@/lib/admin-actions';
import { OrderStatusBadge, formatDate } from '@/components/status-timeline';
import { Badge, Button, Callout, EmptyState, inputClass } from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatCents } from '@/lib/money';
import { ORDER_STATUS_LABEL, ORDER_TRANSITIONS, type Order, type OrderStatus } from '@/lib/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import { adminOrdersQuery } from '@/queries';

/**
 * The order pipeline board. AC-3.5 / plan §8.
 *
 * Columns are the live statuses. Each card offers only the transitions the enum
 * table allows from its current status, and the server action re-checks that
 * table before writing, so the UI cannot produce an illegal move even if it is
 * driven directly.
 *
 * **A card is dragged between columns**, the way the job board already worked.
 * The select stays underneath it, and that is not a duplicate: dragging is fast
 * and imprecise, a select is precise, and one of them works with a keyboard.
 * A move that carries a note has to use the form, because there is nowhere to
 * type on a drag.
 *
 * A column that the enum does not allow from the card's current status refuses
 * the drop and says so, rather than accepting it and having the server refuse
 * a second later. R-196.
 */

/**
 * The columns, which are the statuses that mean work.
 *
 * `delivered` was the fifth column and is gone. It is the terminal state and
 * there are 12,000 of them after the Shopify import: a column that deep is a
 * wall, and the card a person wants is never the one on top. Dragging a card
 * to Shipped still works, and Delivered is still a move the select offers; the
 * order then leaves the board and appears on Orders, which is built for it.
 */
const COLUMNS: OrderStatus[] = ['paid', 'in_production', 'quality_check', 'shipped'];

/**
 * Cards per column before the rest become a count.
 *
 * Twelve is about a screen. The number that matters is that there IS one: a
 * board whose height is set by how well the business has done is a board that
 * stops working exactly when it is needed most.
 */
const COLUMN_CARDS = 12;

/**
 * Lines shown on a card before the rest become a count.
 *
 * Christian, 30 October 2026, from the 29 October meeting: the card shows the
 * lines as stock, as Orders does, so the floor knows the goods at a glance.
 * Four is most orders. A club order of twenty lines is opened, not read on a
 * card.
 */
const CARD_LINES = 4;

/**
 * An order's lines as stock, on its card. Each line opens the order, because
 * the order is what somebody works on, not one garment of it.
 */
function CardLines({ order }: { order: Order }) {
  const lines = order.stock_lines ?? [];
  if (lines.length === 0) {
    return (
      <p className="mt-1.5 text-sm font-semibold leading-snug">
        {order.items[0]?.title}
        {order.items.length > 1 && ` +${order.items.length - 1}`}
      </p>
    );
  }
  const shown = lines.slice(0, CARD_LINES);
  const more = lines.length - shown.length;
  return (
    <ul className="mt-2 space-y-1" aria-label={`Lines of ${order.ref}`}>
      {shown.map((l) => (
        <li key={l.order_item_id}>
          <Link
            to="/admin/orders"
            search={{ open: order.ref }}
            className="block rounded-[6px] px-1.5 py-1 text-[12.5px] leading-snug hover:bg-raised"
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className="font-[family-name:var(--font-mono)] text-[11.5px] text-muted">
                {l.code ?? 'No stock item'}
              </span>
              <span className="font-[family-name:var(--font-mono)] tabular-nums">&times;{l.qty}</span>
            </span>
            <span className="block font-semibold">{l.product ?? l.title}</span>
            {(l.colour || l.size) && (
              <span className="block text-muted">
                {[l.colour, l.size].filter(Boolean).join(' \u00b7 ')}
              </span>
            )}
          </Link>
        </li>
      ))}
      {more > 0 && (
        <li>
          <Link to="/admin/orders" search={{ open: order.ref }} className="px-1.5 text-[12px] text-muted underline">
            {more} more {more === 1 ? 'line' : 'lines'}
          </Link>
        </li>
      )}
    </ul>
  );
}

export default function AdminOrdersPage() {
  const { orders } = useSuspenseQuery(adminOrdersQuery).data;

  /** The order being dragged, and where it is allowed to land. */
  const [dragging, setDragging] = useState<{ ref: string; allowed: OrderStatus[] } | null>(null);
  const [over, setOver] = useState<OrderStatus | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  /**
   * Moving by drag uses the SAME server action the select uses, so the enum
   * table is still what decides. The drop is refused here only to save the
   * round trip and to give a reason on the spot.
   */
  async function moveTo(ref: string, status: OrderStatus) {
    const form = new FormData();
    form.set('ref', ref);
    form.set('status', status);
    await transitionOrderAction(form);
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Pipeline</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Orders still being worked on. Every move writes an event and notifies
          the customer. Finished and refunded orders are under Orders.
          An order with jobs moves by itself: into production when its first job
          starts, and to Quality check when every job is complete.
        </p>
      </div>

      {refused && (
        <div className="mb-4">
          <Callout tone="warn" title={refused} />
        </div>
      )}

      {orders.length === 0 ? (
        <EmptyState title="No orders yet" body="Orders appear here once payment succeeds." />
      ) : (
        <div className="relative -mx-[18px] overflow-x-auto px-[18px]">
          {/* Pulled out by the page's own 18 px, not 20: the 2 px difference
              made the page scroll sideways on a phone (tracker #8).
              `relative`, so the screen-reader label of each card's note box
              stays inside this scroll box. Without it they escaped to the
              page and made it 688 px too wide on a phone, once real orders
              reached the board (pipeline 146, 29 October). */}
          <div className="flex min-w-[1100px] gap-4">
            {COLUMNS.map((column) => {
              const all = orders.filter((o) => o.status === column);

              /**
               * A pipeline is for work that still needs somebody.
               *
               * `delivered` is where an order goes to STOP, and a column that
               * only ever grows is a column nobody reads. The same is true of
               * a "paid" column holding a year of history: the board had 249
               * cards in one column and was 73,735 pixels tall, so the four
               * columns that mattered were three screens below the fold.
               *
               * So every column shows the newest few and says how many it is
               * not showing. Nothing is hidden silently, and nothing is lost:
               * an order is still reachable by its reference.
               */
              const inColumn = all.slice(0, COLUMN_CARDS);
              const hidden = all.length - inColumn.length;
              return (
                <section
                  key={column}
                  className={cn(
                    'flex w-[260px] shrink-0 flex-col rounded-[10px] border bg-raised transition-colors duration-150',
                    dragging && !dragging.allowed.includes(column) && dragging.ref
                      ? 'border-line opacity-50'
                      : 'border-line',
                    over === column && dragging?.allowed.includes(column)
                      ? 'border-[color:var(--color-ink)] bg-deep'
                      : '',
                  )}
                  aria-labelledby={`col-${column}`}
                  onDragOver={(e) => {
                    // Only a column that can accept it says so. A drop zone
                    // that lights up and then refuses is worse than one that
                    // never offered.
                    if (!dragging?.allowed.includes(column)) return;
                    e.preventDefault();
                    setOver(column);
                  }}
                  onDragLeave={() => setOver((c) => (c === column ? null : c))}
                  onDrop={() => {
                    const card = dragging;
                    setOver(null);
                    setDragging(null);
                    if (!card) return;
                    if (!card.allowed.includes(column)) {
                      setRefused(
                        `${card.ref} cannot go straight to ${ORDER_STATUS_LABEL[column]}.`,
                      );
                      return;
                    }
                    setRefused(null);
                    void moveTo(card.ref, column);
                  }}
                >
                  <div className="flex items-center justify-between gap-2 border-b border-line px-3.5 py-3">
                    <h2 id={`col-${column}`} className="text-sm font-bold">
                      {ORDER_STATUS_LABEL[column]}
                    </h2>
                    <span className="font-mono text-sm font-bold text-muted">
                      {inColumn.length}
                    </span>
                  </div>

                  <div className="flex-1 space-y-3 p-3">
                    {inColumn.length === 0 && (
                      <p className="px-1 py-6 text-center text-sm text-muted">Empty</p>
                    )}

                    {hidden > 0 && (
                      <p className="mb-2 rounded-[8px] bg-panel px-3 py-2 text-[12.5px] text-muted">
                        Showing the {inColumn.length} most recent.{' '}
                        <strong className="text-ink">{hidden} older</strong>{' '}
                        {hidden === 1 ? 'order is' : 'orders are'} not shown.
                      </p>
                    )}

                    {inColumn.map((order) => (
                      <article
                        key={order.id}
                        draggable={ORDER_TRANSITIONS[order.status].length > 0}
                        onDragStart={() => {
                          setRefused(null);
                          setDragging({
                            ref: order.ref,
                            allowed: [...ORDER_TRANSITIONS[order.status]],
                          });
                        }}
                        onDragEnd={() => { setDragging(null); setOver(null); }}
                        className={cn(
                          'rounded-[10px] border border-line bg-panel p-3.5',
                          ORDER_TRANSITIONS[order.status].length > 0
                            ? 'cursor-grab active:cursor-grabbing'
                            : '',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <Link
                            to="/admin/orders"
                            search={{ open: order.ref }}
                            className="font-mono text-sm font-bold text-brand-ink underline-offset-2 hover:underline"
                          >
                            {order.ref}
                          </Link>
                          {order.channel !== 'web' && (
                            <Badge tone="brand">{order.channel.toUpperCase()}</Badge>
                          )}
                        </div>
                        <CardLines order={order} />
                        <p className="mt-0.5 text-[13px] text-muted">
                          {formatDate(order.created_at)}
                        </p>
                        <p className="mt-1.5 font-[family-name:var(--font-mono)] text-[13px] font-medium tabular-nums">
                          {formatCents(order.total_cents)}
                        </p>

                        {/* Said on the card, because the pipeline has a column
                            called "In production" and a stock sale that lands
                            in it makes no job. Two screens that disagree about
                            what a word means is a seam, not a misreading.
                            R-196. */}
                        {/* Spec 015 FR-1530. The order follows its jobs: the
                            first job that moves on takes a paid order into
                            production, and the last one to complete takes it
                            to Quality check. The card says how far they are. */}
                        {(order.jobs_total ?? 0) > 0 && (
                          <p className="mt-1.5 text-[12px] text-muted">
                            Jobs: {order.jobs_complete ?? 0} of {order.jobs_total} complete
                          </p>
                        )}

                        {order.status === 'in_production' && !order.has_job && (
                          <p className="mt-1.5 text-[11px] text-muted">
                            Picked and packed here. No decoration, so no job.
                          </p>
                        )}

                        {ORDER_TRANSITIONS[order.status].length > 0 && (
                          <form
                            action={transitionOrderAction}
                            className="mt-3 space-y-2 border-t border-line pt-3"
                          >
                            <input type="hidden" name="ref" value={order.ref} />
                            <label className="sr-only" htmlFor={`status-${order.id}`}>
                              Move {order.ref} to
                            </label>
                            <select
                              id={`status-${order.id}`}
                              name="status"
                              className={`${inputClass} h-10 px-2 py-0 text-sm`}
                              defaultValue={ORDER_TRANSITIONS[order.status][0]}
                            >
                              {ORDER_TRANSITIONS[order.status].map((s) => (
                                <option key={s} value={s}>
                                  {ORDER_STATUS_LABEL[s]}
                                </option>
                              ))}
                            </select>
                            <label className="sr-only" htmlFor={`note-${order.id}`}>
                              Note for {order.ref}
                            </label>
                            <input
                              id={`note-${order.id}`}
                              name="note"
                              placeholder="Note (optional)"
                              className={`${inputClass} h-10 py-0 text-sm`}
                            />
                            <Button type="submit" weight="secondary" size="sm" className="w-full">
                              Move
                            </Button>
                          </form>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}

      {/*
        * Cancelled and refunded used to be listed here, under the board.
        *
        * It was a section that read the board's own rows, so the moment the
        * board narrowed to work in progress it said "Nothing cancelled or
        * refunded" over a book holding 20 refunds. An empty state that is
        * simply wrong is worse than no section: it answers the question
        * confidently and incorrectly.
        *
        * Orders has a Refunded filter and a Cancelled filter, both carrying a
        * count from the database. That is where the answer lives now.
        */}
    </div>
  );
}
